import {
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES,
  operationalMonthsSeparator,
  weeksPerMonthValue,
  weeksPerYearValue,
} from 'common/constants';
import { isValuePresent } from 'helpers/utils';

import { PAYROLL_WORKSHEET_FIELD_CODES } from '../PayrollWorksheetContainer';

const YEAR_ROUND_FIELD_CODES = EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES;

interface PayrollWorksheetTotals {
  fullTimeEstimatedAnnualPayroll: number;
  partTimeEstimatedAnnualPayroll: number;
  yearRoundFtEstimatedAnnualPayroll: number;
  yearRoundPtEstimatedAnnualPayroll: number;
  totalEmployeeCount: number;
  totalEstimatedPayroll: number;
  hasValidBreakdown: boolean;
  hasWorksheetData: boolean;
}

export const computePayrollWorksheetTotals = (
  state: Record<string, unknown>,
  isExpandedHotel: boolean = false,
  // eslint-disable-next-line complexity
): PayrollWorksheetTotals => {
  const isSeasonal = state?.[PAYROLL_WORKSHEET_FIELD_CODES.isSeasonalBusiness] === 'Yes';
  const operationalMonthsValue = (state?.[PAYROLL_WORKSHEET_FIELD_CODES.operationalMonths] ??
    '') as string;
  const selectedMonths: string[] = operationalMonthsValue
    ? operationalMonthsValue.split(operationalMonthsSeparator).filter(Boolean)
    : [];

  const seasonalWeeks =
    isSeasonal && selectedMonths.length > 0
      ? selectedMonths.length * weeksPerMonthValue
      : weeksPerYearValue;

  const fullTimeEmployeeCount = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeEmployeeCount] ?? 0,
  );
  const fullTimeAvgHourlyWage = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeAvgHourlyWage] ?? 0,
  );
  const fullTimeAvgWeeklyHours = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeAvgWeeklyHours] ?? 0,
  );

  const fullTimeAnnualPayroll = Math.round(
    fullTimeEmployeeCount * fullTimeAvgHourlyWage * fullTimeAvgWeeklyHours * seasonalWeeks,
  );

  const partTimeEmployeeCount = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeEmployeeCount] ?? 0,
  );
  const partTimeAvgHourlyWage = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeAvgHourlyWage] ?? 0,
  );
  const partTimeAvgWeeklyHours = Number(
    state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeAvgWeeklyHours] ?? 0,
  );

  const partTimeAnnualPayroll = Math.round(
    partTimeEmployeeCount * partTimeAvgHourlyWage * partTimeAvgWeeklyHours * seasonalWeeks,
  );

  let yearRoundFtEmployeeCount = 0;
  let yearRoundFtEstimatedAnnualPayroll = 0;
  let yearRoundPtEmployeeCount = 0;
  let yearRoundPtEstimatedAnnualPayroll = 0;
  let yearRoundFtAnyFilled = false;
  let yearRoundPtAnyFilled = false;
  let yearRoundFtGroupValid = true;
  let yearRoundPtGroupValid = true;

  if (isExpandedHotel) {
    yearRoundFtEmployeeCount = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtEmployeeCount] ?? 0,
    );
    const yearRoundFtAvgHourlyWage = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtAvgHourlyWage] ?? 0,
    );
    const yearRoundFtAvgWeeklyHours = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtAvgWeeklyHours] ?? 0,
    );
    yearRoundFtEstimatedAnnualPayroll = Math.round(
      yearRoundFtEmployeeCount *
        yearRoundFtAvgHourlyWage *
        yearRoundFtAvgWeeklyHours *
        weeksPerYearValue,
    );

    yearRoundPtEmployeeCount = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtEmployeeCount] ?? 0,
    );
    const yearRoundPtAvgHourlyWage = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtAvgHourlyWage] ?? 0,
    );
    const yearRoundPtAvgWeeklyHours = Number(
      state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtAvgWeeklyHours] ?? 0,
    );
    yearRoundPtEstimatedAnnualPayroll = Math.round(
      yearRoundPtEmployeeCount *
        yearRoundPtAvgHourlyWage *
        yearRoundPtAvgWeeklyHours *
        weeksPerYearValue,
    );

    const yearRoundFtFieldsFilled = [
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtEmployeeCount]),
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtAvgHourlyWage]),
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundFtAvgWeeklyHours]),
    ];
    const yearRoundPtFieldsFilled = [
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtEmployeeCount]),
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtAvgHourlyWage]),
      isValuePresent(state?.[YEAR_ROUND_FIELD_CODES.yearRoundPtAvgWeeklyHours]),
    ];
    yearRoundFtAnyFilled = yearRoundFtFieldsFilled.some(Boolean);
    yearRoundPtAnyFilled = yearRoundPtFieldsFilled.some(Boolean);
    const yearRoundFtAllFilled = yearRoundFtFieldsFilled.every(Boolean);
    const yearRoundPtAllFilled = yearRoundPtFieldsFilled.every(Boolean);
    yearRoundFtGroupValid =
      yearRoundFtEmployeeCount === 0 || yearRoundFtAllFilled || !yearRoundFtAnyFilled;
    yearRoundPtGroupValid =
      yearRoundPtEmployeeCount === 0 || yearRoundPtAllFilled || !yearRoundPtAnyFilled;
  }

  const fullTimeFieldsFilled = [
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeEmployeeCount]),
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeAvgHourlyWage]),
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.fullTimeAvgWeeklyHours]),
  ];
  const fullTimeAnyFilled = fullTimeFieldsFilled.some(Boolean);
  const fullTimeAllFilled = fullTimeFieldsFilled.every(Boolean);

  const partTimeFieldsFilled = [
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeEmployeeCount]),
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeAvgHourlyWage]),
    isValuePresent(state?.[PAYROLL_WORKSHEET_FIELD_CODES.partTimeAvgWeeklyHours]),
  ];
  const partTimeAnyFilled = partTimeFieldsFilled.some(Boolean);
  const partTimeAllFilled = partTimeFieldsFilled.every(Boolean);

  const fullTimeGroupValid = fullTimeEmployeeCount === 0 || fullTimeAllFilled || !fullTimeAnyFilled;
  const partTimeGroupValid = partTimeEmployeeCount === 0 || partTimeAllFilled || !partTimeAnyFilled;

  const hasValidBreakdown =
    fullTimeGroupValid && partTimeGroupValid && yearRoundFtGroupValid && yearRoundPtGroupValid;
  const hasWorksheetData =
    fullTimeAnyFilled || partTimeAnyFilled || yearRoundFtAnyFilled || yearRoundPtAnyFilled;

  return {
    fullTimeEstimatedAnnualPayroll: fullTimeAnnualPayroll,
    partTimeEstimatedAnnualPayroll: partTimeAnnualPayroll,
    yearRoundFtEstimatedAnnualPayroll,
    yearRoundPtEstimatedAnnualPayroll,
    totalEmployeeCount:
      fullTimeEmployeeCount +
      partTimeEmployeeCount +
      yearRoundFtEmployeeCount +
      yearRoundPtEmployeeCount,
    totalEstimatedPayroll:
      fullTimeAnnualPayroll +
      partTimeAnnualPayroll +
      yearRoundFtEstimatedAnnualPayroll +
      yearRoundPtEstimatedAnnualPayroll,
    hasValidBreakdown,
    hasWorksheetData,
  };
};
